import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Button as MuiButton,
  Select,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { Visibility } from "@material-ui/icons";
import Close from "@material-ui/icons/Close";
import { Typography } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/Forms/CustomButtons/ButtonTS";
import Table from "components/Tables/ReactTable/ReactTable";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { DocumentAttachment } from "services/input-core/document-attachment/@types";
import { useManualValidationApprovers } from "services/input-core/manual-validation/get-manual-validation-approvers";
import { useManualValidation } from "services/input-core/manual-validation/manual-validation";
import ExpirationDateModal from "../ExpirationDateModal";
import { useGetPaginatedDocumentAttachments } from "services/input-core/document-attachment/get-paginated-document-attachments";
import { useTranslation } from "react-i18next";
import { checkMessageErrors } from "utils/functions";
import { toast } from "react-toastify";
import { TagHeaderModal } from "components/TagHeaderModal";
import { useActivityItemContext } from "store/activityItem";

interface ApprovalModalProps {
  profile: "WORKER" | "SOW";
  modalOpen: boolean;
  fieldglassId?: string;
  onClose: () => void;
  entityDate?: {
    start: string;
    end: string;
  };
}

async function openImageInNewTab(fileUri: string, doc: DocumentAttachment) {
  if (!fileUri) return;

  if (fileUri) {
    const { data } = await axios.get(fileUri, {
      responseType: "blob",
    });

    const metadata = {
      type: doc?.mimeType,
      disposition: "inline",
    };

    const file = new File([data], "file", metadata);

    const fileURL = URL.createObjectURL(file);

    window.open(fileURL);
  }
}

const ActionButtons: React.FC<{
  fileUri: string;
  doc?: DocumentAttachment;
  disabled?: boolean;
  isSupplier: boolean;
  handleDelete: () => void;
}> = ({ fileUri, doc }) => {
  return (
    <div className="actions-right">
      <Tooltip title="Preview" key={0}>
        <Button
          disabled={!fileUri}
          justIcon
          color="primary"
          round
          target="_blank"
          onClick={() => openImageInNewTab(fileUri, doc)}
          size="sm"
        >
          <Visibility />
        </Button>
      </Tooltip>
    </div>
  );
};

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  modalOpen,
  onClose,
  fieldglassId,
  entityDate,
  profile,
}) => {
  const { t } = useTranslation(["modals", "list-errors", "list-messages"]);

  const { currentActivityItem } = useActivityItemContext((state) => ({
    currentActivityItem: state.currentActivity,
  }));

  const columns: {
    Header: string;
    accessor: string;
  }[] = [
    {
      Header: t("react-table-column-name", { ns: "modals" }),
      accessor: "name",
    },
    {
      Header: t("react-table-column-uploaded-by"),
      accessor: "uploadedBy",
    },
    {
      Header: t("react-table-column-uploaded-at", { ns: "modals" }),
      accessor: "uploadedAt",
    },
    {
      Header: t("react-table-column-actions", { ns: "modals" }),
      accessor: "actions",
    },
  ];

  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();
  const isBuyer = keycloak?.hasResourceRole("buyer", "uverify");
  const isSupplier = keycloak?.hasResourceRole("supplier", "uverify");

  const refId =
    fieldglassId ?? new URLSearchParams(window.location.search).get("id");

  const [expirationDateModalOpen, setExpirationDateModalOpen] = React.useState(
    false
  );

  const { register, watch, setValue, reset } = useForm();

  const [isStepOneCompleted, setIsStepOneCompleted] = React.useState<boolean>(
    false
  );

  const [comment, setComment] = React.useState<string>("");
  const [roles, setRoles] = React.useState([]);

  const { data: documentsFromActivity } = useGetPaginatedDocumentAttachments({
    active: true,
    zID: currentActivityItem?.zIdentifier,
    fieldglassId: currentActivityItem?.fieldglassId || fieldglassId,
    params: {
      limit: 10,
      page: 1,
    },
  });

  const {
    mutate: manualValidation,
    isLoading: isSavingManualValidation,
  } = useManualValidation();

  const { data: manualValidationApprovers } = useManualValidationApprovers(
    currentActivityItem.activityItemCode
  );

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    reset();
    if (
      currentActivityItem.manualApproval === undefined ||
      currentActivityItem.manualApproval === null
    )
      return;

    // for each key and value in manualApproval
    for (const [key, value] of Object.entries(
      currentActivityItem.manualApproval
    )) {
      setValue(key, value);
    }
  }, [currentActivityItem]);

  const nextStep = () => {
    setIsStepOneCompleted(true);
  };

  const getManualValidationValueByRoles = () =>
    roles.every((role) => watch(role) === "Approved") ? "Approved" : "Rejected";

  const handleValidate = (
    validationExpirationDate?: Date,
    validationExpirationStartDate?: Date,
  ) => {
    manualValidation(
      {
        fieldglassId: currentActivityItem.fieldglassId,
        activityItemCode: currentActivityItem.activityItemCode,
        subscriptionId: currentActivityItem.subscription,
        zIdentifier: currentActivityItem.zIdentifier,
        roles: roles,
        manualValidationValue: getManualValidationValueByRoles(),
        reasonForRejection: watch("rejectedReason"),
        validationExpirationDate,
        ...(currentActivityItem?.hasStartDate && {
          validationExpirationStartDate,
        }),
      },
      {
        onSuccess: () => {
          toast.success(t("success-validate", { ns: "list-messages" }));
          queryClient.invalidateQueries([
            profile == "SOW"
              ? "activity-items-sow"
              : profile == "WORKER" && "activity-items-worker",
            refId,
          ]);
          onClose();
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_VALIDATE_DEFAULT"
              ),
              { ns: "list-errors" }
            )
          );
        },
      }
    );
  };

  const hasRole = () => {
    let hasRole = false;

    manualValidationApprovers?.forEach((role) => {
      hasRole =
        hasRole ||
        keycloak.hasRealmRole(role) ||
        keycloak.hasResourceRole(role, "uverify");
    });

    return hasRole;
  };

  const allApproved: boolean =
    manualValidationApprovers?.reduce((prev, cur) => {
      if (prev === false) return prev;

      const isAllApproved: boolean = watch(cur) === "Approved";

      return isAllApproved;
    }, true) || false;

  const renderListAndApprovalStep = (isUploading: boolean) => {
    return (
      <>
        <DialogContent style={{ padding: "40px" }}>
          <form action="">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                alignItems: "center",
              }}
            >
              {manualValidationApprovers &&
                manualValidationApprovers?.map((approver: string) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "40px",
                      }}
                    >
                      <MuiButton
                        style={{ height: "50%", alignSelf: "center" }}
                        variant="contained"
                        color="primary"
                        disableElevation
                        disabled={
                          !(
                            (currentActivityItem.manualApproval === undefined ||
                              currentActivityItem.manualApproval === null ||
                              currentActivityItem.manualApproval[approver] ===
                                undefined ||
                              currentActivityItem.manualApproval[approver] ===
                                "Waiting") &&
                            keycloak.hasRealmRole(approver)
                          )
                        }
                      >
                        {approver}
                      </MuiButton>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <FormControl
                          style={{
                            minWidth: 200,
                          }}
                        >
                          <InputLabel>{t("documents-approval")}</InputLabel>
                          <Select
                            variant="standard"
                            disabled={
                              !(
                                (currentActivityItem.manualApproval ===
                                  undefined ||
                                  currentActivityItem.manualApproval === null ||
                                  currentActivityItem.manualApproval[
                                    approver
                                  ] === undefined ||
                                  currentActivityItem.manualApproval[
                                    approver
                                  ] === "Waiting") &&
                                keycloak.hasRealmRole(approver)
                              )
                            }
                            {...register(approver, {
                              required: true,
                              onChange: (event) => {
                                if (event.target.value === "none") {
                                  setRoles((ps: string[]) =>
                                    ps.filter((role) => role !== approver)
                                  );
                                } else {
                                  setRoles((ps: string[]) => [...ps, approver]);
                                  setValue(approver, event.target.value);
                                }
                              },
                            })}
                            value={watch(approver) || "none"}
                          >
                            <MenuItem value="none">
                              <em>{t("choose-from-list")}</em>
                            </MenuItem>
                            <MenuItem value={"Approved"}>
                              {`${t("approved")}`}
                            </MenuItem>
                            <MenuItem value={"Rejected"}>
                              {`${t("rejected")}`}
                            </MenuItem>
                          </Select>
                        </FormControl>
                        {watch(approver) === "Rejected" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              required
                              label="Reject Reason"
                              placeholder="Reject Reason"
                              multiline
                              style={{ marginLeft: 30, marginBottom: 10 }}
                              {...register("rejectedReason", {
                                required: true,
                              })}
                            />
                            {!watch("rejectedReason") && (
                              <Typography key="rejectedReason" color="error">
                                {t("this-field-is-required")}
                              </Typography>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </form>
        </DialogContent>

        <Divider style={{ backgroundColor: "#808080" }} />
        <DialogContent>
          <Table
            columns={columns}
            data={dataFormatted}
            initialPageSize={5}
            columnsNotTrimFilters={["name", "uploadedBy"]}
          />

          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "24px",
              marginBottom: "24px",
            }}
          >
            <Button
              color="primary"
              disabled={
                isUploading ||
                !hasRole() ||
                !roles ||
                roles.length === 0 ||
                (Object.values(watch()).some(
                  (element) => element === "Rejected"
                ) &&
                  !watch("rejectedReason"))
              }
              onClick={
                allApproved
                  ? () => setExpirationDateModalOpen(true)
                  : () => handleValidate()
              }
              isLoading={isUploading}
            >
              {t("conclude")}
            </Button>
          </div>
        </DialogContent>
      </>
    );
  };

  const renderConfirmNotApprovalStep = (isUploading: boolean) => {
    return (
      <>
        <DialogContent style={{ padding: "40px" }}>
          <form action="">
            <TextField
              id="rejectDescription"
              label="Reason for rejection"
              multiline
              rows={5}
              fullWidth
              required
              variant="outlined"
              style={{ padding: "10px" }}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </form>
        </DialogContent>

        <DialogContent>
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              gap: "8px",
              marginBottom: "24px",
            }}
          >
            {!isBuyer && (
              <Button
                color="primary"
                onClick={() => nextStep()}
                disabled={isUploading}
                isLoading={isUploading}
              >
                {t("conclude")}
              </Button>
            )}
          </div>
        </DialogContent>
      </>
    );
  };

  const dataFormatted =
    documentsFromActivity?.docs?.map((doc, index) => {
      return {
        name: doc.name,
        uploadedBy: `${doc?.createdBy?.name} (${doc?.createdBy?.email})`,
        uploadedAt: new Date(doc.createdAt).toLocaleString(),
        fromApi: true,
        actions: (
          <ActionButtons
            fileUri={doc.uri}
            doc={doc}
            key={index}
            isSupplier={isSupplier}
            handleDelete={() => null}
          />
        ),
      };
    }) || [];

  return (
    <>
      <ExpirationDateModal
        modalOpen={expirationDateModalOpen}
        onClose={() => setExpirationDateModalOpen(false)}
        onConfirm={handleValidate}
        isUploading={isSavingManualValidation}
        entityDate={entityDate}
      />
      <Dialog
        open={modalOpen}
        fullWidth
        maxWidth={!isStepOneCompleted ? "md" : "sm"}
        scroll="paper"
        onClose={() => {
          onClose();
        }}
        hideBackdrop
      >
        <DialogTitle>
          {t("validate-documents", { ns: "modals" })}
          <Button
            justIcon
            simple
            color="tumblr"
            round
            size="sm"
            style={{
              position: "absolute",
              right: "10px",
              top: "10px",
            }}
            onClick={onClose}
          >
            <Close />
          </Button>
        </DialogTitle>
        <Divider style={{ backgroundColor: "#808080" }} />
        <TagHeaderModal
          tagsHeader={[
            {
              label: t("activity-code", { ns: "activity-item-table" }),
              content: currentActivityItem.activityItemCode,
            },
            {
              label: t("date", { ns: "activity-item-table" }),
              content: currentActivityItem.activityItemDueDate,
            },
            {
              label: t("action", { ns: "activity-item-table" }),
              content: currentActivityItem.activityItemAction,
              isolated: true,
            },
          ]}
        />
        <Divider style={{ backgroundColor: "#808080" }} />
        {!isStepOneCompleted
          ? renderListAndApprovalStep(isSavingManualValidation)
          : renderConfirmNotApprovalStep(isSavingManualValidation)}
      </Dialog>
    </>
  );
};

export default ApprovalModal;
